import { useCallback, useMemo, useState } from 'react'
import {
  ActivityFeed,
  ActivityFeedDataTypes,
  Button,
  FeaturedIconColorType,
  FileInfoBox,
  Flex,
  Spacer,
  Textarea,
  Avatars,
} from '@jisr-hr/ds-beta'
import { format } from 'utils/date'
import i18n from 'translations/i18n'
import { useSelector } from 'react-redux'
import {
  useAddComment,
  useAddReaction,
  useRemoveReaction,
} from 'apis/OrgManagement/announcementSegrigation/useAnnouncementSeg'
import {
  getCommentsKeyWithPagination,
  useGetCommentsWithPagination,
} from 'apis/OrgManagement/Announcement/useAnnouncement'
import { queryClient } from 'hooks/useReactQuery'
import { CommentType } from 'apis/OrgManagement/Announcement/useAnnouncement.Type'
import {
  TAnnouncement,
  TAnnouncementAttachment,
} from 'apis/OrgManagement/announcementSegrigation/useAnnouncementSeg.type'
import AnnouncementWidgetFollowUpBody from './announcementWidgetFollowUpBody'
import ReactionsButton from './reactionsButton'

interface AnnouncementWidgetBodyProps {
  announcementData: TAnnouncement
  refetch: () => void
  width: number
}

const AnnouncementWidgetBody = ({
  announcementData,
  refetch,
  width,
}: AnnouncementWidgetBodyProps) => {
  const { employee } = useSelector(({ auth }) => auth)
  const announcement = announcementData
  const [commentText, setCommentText] = useState<string>('')
  const { mutate: addComment } = useAddComment()
  const { mutate: addReaction } = useAddReaction()
  const { mutate: removeReaction } = useRemoveReaction()
  const [showCommentArea, setShowCommentArea] = useState(false)
  const handleLeaveComment = () => {
    if (announcement?.id) {
      addComment(
        {
          comment: {
            commentable_type: 'Announcement',
            commentable_id: announcement.id,
            content: commentText,
          },
        },
        {
          onSuccess: () => {
            setCommentText('')
            refetch()
          },
        },
      )
    }
  }
  const handleAddReaction = (comment: CommentType) => {
    if (comment?.reaction?.id) {
      removeReaction(
        {
          reaction: {
            reactable_id: comment?.reaction?.id,
            reaction_type: 'like',
            reactable_type: 'Comment',
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([getCommentsKeyWithPagination()[0]])
          },
        },
      )
    } else {
      addReaction(
        {
          reaction: {
            reactable_id: comment.id,
            reaction_type: 'like',
            reactable_type: 'Comment',
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([getCommentsKeyWithPagination()[0]])
          },
        },
      )
    }
  }
  const canTakeAction = announcement?.can_take_action
  const {
    data: { pages = [] } = {},
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGetCommentsWithPagination({
    query: {
      select: (s) => ({
        pages: s.pages,
        pageParams: s.pageParams,
      }),
    },
    params: { commentable_type: 'Announcement', commentable_id: announcement.id, rpp: 2 },
  })

  const comments = useMemo(() => pages.flatMap((page) => page?.data?.data?.comments), [pages])

  const announcementComments = useMemo(
    () =>
      comments?.map((it) => ({
        date: it.display_time,
        items: [
          {
            time: format(it.created_at, 'hh:mm a') as string,
            avatarProps: {
              imageSrc: it.employee.avatar_thumb ?? '',
            },
            header: {
              title: it.employee.name,
              badges: [
                {
                  color: 'gray',
                  leadingIcon: 'thumbs-up',
                  count: 99,
                  label: `${it.reactions_count > 99 ? '+99' : it.reactions_count ?? 0}`,
                },
              ],
            },
            comment: {
              text: it.content,
              trailingAction: (
                <Button
                  iconOnly
                  variant="tinted"
                  color={it.reaction?.id ? 'primary' : 'neutral'}
                  leadingIcon="thumbs-up"
                  onClick={() => {
                    handleAddReaction(it)
                  }}
                />
              ),
            },
            variant: 'inline',
            iconProps: {
              iconName: 'message-circle-01',
              colorType: 'success' as FeaturedIconColorType,
            },
          },
        ],
      })) ?? [],
    [comments],
  )
  const handleClick = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, fetchNextPage])
  return (
    <div>
      <div className="gap-12 contents">
        {announcement?.attachments?.map((attachment: TAnnouncementAttachment, index: number) => (
          <>
            {attachment?.content_type === 'image/png' ? (
              <img
                key={index}
                className="h-[294px] w-[712px] border-radius-[12px] object-contain mb-4"
                src={attachment?.url}
                alt={attachment.name}
              />
            ) : (
              <>
                <FileInfoBox
                  key={index}
                  file={{
                    status: 'success',
                    type: attachment.content_type,
                    name: attachment.name,
                    preview: attachment.url,
                    url: attachment.url,
                  }}
                  withPreview
                  onHandleDownload={() => {
                    window.open(attachment.url, '_blank')
                  }}
                />
              </>
            )}
          </>
        ))}
      </div>
      <Spacer height={12} />
      <Flex className="gap-2">
        {canTakeAction && (
          <ReactionsButton
            refetch={refetch}
            item={announcement}
            width={width}
          />
        )}
        {announcement?.allow_comment && canTakeAction && (
          <Button
            label={`${i18n.t('comment')} (${announcement?.comments_count ?? 0})`}
            color="neutral"
            variant="outlined"
            leadingIcon="message-square-02"
            size="small"
            onClick={() => {
              setShowCommentArea(true)
            }}
          />
        )}
      </Flex>
      <Spacer height={12} />
      {announcement?.comments_count > 0 && canTakeAction && (
        <Button
          size="small"
          label={i18n.t('action.view_more_comments')}
          onClick={handleClick}
          loading={isLoading || isFetchingNextPage}
          className="mt-4 mb-3"
          variant="ghost"
          color="primary"
        />
      )}

      <Spacer height={12} />
      {comments && (
        <ActivityFeed
          timePosition="start"
          data={announcementComments as unknown as ActivityFeedDataTypes[]}
        />
      )}
      {showCommentArea && (
        <Flex style={{ gap: 12 }}>
          <Avatars
            imageSrc={employee?.avatar_thumb}
            imageAlt={employee?.full_name_i18n}
          />
          <Textarea
            onChange={(value) => setCommentText(value)}
            value={commentText}
            resizable
          />
          <Button
            label={i18n.t('action.publish_comment')}
            color="primary"
            variant="tinted"
            size="medium"
            disabled={!commentText}
            onClick={handleLeaveComment}
          />
        </Flex>
      )}
      <Spacer height={12} />
      {announcement.follow_ups?.map((followUp, i) => (
        <AnnouncementWidgetFollowUpBody
          key={i}
          followUp={followUp}
          refetch={refetch}
        />
      ))}
    </div>
  )
}

export default AnnouncementWidgetBody
